import type { AddDeviceFormType } from "@/apps/partner/types";
import { FormOfAddresses } from "@/components/Onboarding/Steps/PersonalDetails/PersonalDetailsFormFields/PersonalDetailsFormFields";
import {
  SalesPartnerCreateCustomerLanguageEnum,
  SalesPartnerCreateCustomerSalutationEnum,
} from "@/services/model";
import type { HeaderModel } from "@/services/model/headerModel";
import type { LookupValue } from "@/services/model/lookupValue";
import type { MnpData } from "@/services/model/mnpData";
import type { Phone } from "@/services/model/phone";
import type { SalesPartnerCreateCustomer } from "@/services/model/salesPartnerCreateCustomer";
import type { SalesPartnerOrderSimRequestOut } from "@/services/model/salesPartnerOrderSimRequestOut";
import type { SalesPartnerOrderSimRequestOutDevice } from "@/services/model/salesPartnerOrderSimRequestOutDevice";
import type { SalesPartnerOrderSimRequestOutSimType } from "@/services/model/salesPartnerOrderSimRequestOutSimType";

/**
 * Transforms AddDeviceFormType to SalesPartnerOrderSimRequestOut
 * @param formData - The form data from AddDevice component
 * @param headerModel - Required header model for the customer data
 * @returns Transformed data ready for the API
 */
export const transformFormDataToApiRequest = (
  formData: AddDeviceFormType,
  headerModel: HeaderModel,
): SalesPartnerOrderSimRequestOut => {
  // Transform customer data
  const customer: SalesPartnerCreateCustomer = {
    firstName: formData.customer.firstName,
    lastName: formData.customer.lastName,
    contactPhoneNumber: formData.customer.contactPhoneNumber,
    city: formData.customer.city,
    countryCodeAlpha2: formData.customer.countryCodeAlpha2.ALPHA2,
    houseNumber: formData.customer.houseNumber,
    street: formData.customer.street,
    zip: formData.customer.zip,
    // TODO: Add postalDeliveryNotes when backend is updated
    // postalDeliveryNotes: formData.customer.postalDeliveryNotes,
    languageEnum: formData.customer
      .languageEnum as SalesPartnerCreateCustomerLanguageEnum,
    salutationEnum:
      formData.customer.salutationEnum === FormOfAddresses.other.toString()
        ? SalesPartnerCreateCustomerSalutationEnum.MX
        : formData.customer.salutationEnum === FormOfAddresses.mr.toString()
          ? SalesPartnerCreateCustomerSalutationEnum.MR
          : SalesPartnerCreateCustomerSalutationEnum.MS,
    birthday: formData.customer.birthday,
    header: headerModel,
  };

  // Transform MNP data if present
  let mnpData: MnpData | undefined;
  if (formData.mnpData) {
    const currentProvider: LookupValue = {
      id: formData.mnpData.currentProvider.id,
      description: formData.mnpData.currentProvider.description,
    };

    const mnpTime: LookupValue = {
      id: formData.mnpData.mnpTime.id,
      description: formData.mnpData.mnpTime.description,
    };

    const mnpContractType: LookupValue = {
      id: formData.mnpData.mnpContractType.id,
      description: formData.mnpData.mnpContractType.description,
    };

    const portedPhoneNumber: Phone = {
      phone: formData.mnpData.portedPhoneNumber.phone,
    };

    mnpData = {
      currentProvider,
      mnpTime,
      mnpContractType,
      portedPhoneNumber,
      msisdn: formData.mnpData.portedPhoneNumber.phone,
    };
  }

  // Transform device type
  let device: SalesPartnerOrderSimRequestOutDevice | undefined;
  if (formData.device) {
    device = formData.device as SalesPartnerOrderSimRequestOutDevice;
  }

  // Transform sim type
  let simType: SalesPartnerOrderSimRequestOutSimType | undefined;
  if (formData.simType) {
    simType = formData.simType as SalesPartnerOrderSimRequestOutSimType;
  }

  // Transform onp from string to boolean
  const onp = formData.onp === "true";

  return {
    customer,
    device,
    iccid: formData.iccid,
    mail: formData.mail,
    mnpData,
    onp,
    productId: formData.productId,
    puk: formData.puk,
    simAlias: formData.simAlias,
    simType,
  };
};
